import React from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import ButtonItem from '@atlaskit/menu/button-item';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	DROPDOWN,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { ModalEntryPointTrigger } from './ModalEntryPointTrigger.tsx';

const button = defineMessage({
	id: 'polaris-component-ai-view-generation.button.non-final',
	defaultMessage: 'Generate view',
	description: 'Dropdown option to open AI View Generation',
});

const iconLabel = defineMessage({
	id: 'polaris-component-ai-view-generation.iconLabel.non-final',
	defaultMessage: 'Generate view',
	description: 'Label for the icon of the Generate views button',
});

const lozenge = defineMessage({
	id: 'polaris-component-ai-view-generation.lozenge.non-final',
	defaultMessage: 'New',
	description:
		'Text shown in a lozenge badge next to the AI view generation menu button, indicating the feature is newly available.',
});

type AiGenerateViewsMenuButtonProps = {
	onClick?: () => void;
	projectId: string;
	projectKey: string;
	containerId: string | undefined;
};

export const AiGenerateViewsMenuButton = ({
	onClick,
	projectId,
	projectKey,
	containerId,
}: AiGenerateViewsMenuButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<JSErrorBoundary
			id="aiViewGeneration"
			packageName="jiraAiViewGenerationButton"
			teamName="jpd-juno"
			fallback="unmount"
		>
			<ContextualAnalyticsData sourceName="aiViewGenerationItem" sourceType={DROPDOWN}>
				<ModalEntryPointTrigger
					projectId={projectId}
					projectKey={projectKey}
					containerId={containerId}
				>
					{({ ref }) => (
						<ButtonItem
							iconBefore={<AtlassianIntelligenceIcon label={formatMessage(iconLabel)} />}
							onClick={onClick}
							ref={ref}
						>
							<Inline alignBlock="center" spread="space-between" space="space.100">
								<Box>{formatMessage(button)}</Box>
								<Lozenge
									appearance={
										fg('platform-dst-lozenge-tag-badge-visual-uplifts') ? 'discovery' : 'new'
									}
								>
									{formatMessage(lozenge)}
								</Lozenge>
							</Inline>
						</ButtonItem>
					)}
				</ModalEntryPointTrigger>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
};
